import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { clearDraftKey, clearDrafts, loadDraftKey } from "@/lib/drafts";

const AuthContext = createContext(null);

const LOGOUT_PENDING = "pensive_logout_pending";

// a sign-out made without connection is completed with the server before anything else
const finishPendingLogout = async () => {
  if (!localStorage.getItem(LOGOUT_PENDING)) return true;
  try {
    await api.post("/auth/logout");
  } catch (e) {
    if (e.response?.status !== 401) return false;
  }
  localStorage.removeItem(LOGOUT_PENDING);
  return true;
};

export const AuthProvider = ({ children }) => {
  // null = checking, false = signed out, object = signed in
  const [user, setUser] = useState(null);

  const checkAuth = useCallback(async () => {
    if (localStorage.getItem(LOGOUT_PENDING)) {
      await finishPendingLogout();
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Returning from Google: AuthCallback exchanges the session first
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    window.addEventListener("online", finishPendingLogout);
    return () => window.removeEventListener("online", finishPendingLogout);
  }, []);

  useEffect(() => {
    if (!user?.user_id) return;
    localStorage.removeItem(LOGOUT_PENDING);
    loadDraftKey(user.user_id);
  }, [user?.user_id]);

  const logout = async () => {
    if (user?.user_id) await clearDrafts(user.user_id);
    clearDraftKey();
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (!e.response) localStorage.setItem(LOGOUT_PENDING, "1");
    } finally {
      setUser(false);
    }
  };

  return <AuthContext.Provider value={{ user, setUser, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
